interface StoryItemButton {
  url: string;
  label: string;
  type?: 'button' | 'large' | 'link' | string;
}

interface StoryImageSource {
  id?: number;
  url?: string;
  alt?: string;
}

interface StoryImage {
  desktop?: StoryImageSource;
  mobile?: StoryImageSource;
}

interface StoryItem {
  title: string;
  text: string; // May contain HTML paragraph markup
  image: StoryImage;
  button?: StoryItemButton;
}

interface StorytellingModule {
  els: {
    root: HTMLElement[];
  };
  init: () => void;
  bindDesktop: (root: HTMLElement) => void;
  bindMobile: (root: HTMLElement) => void;
  bindHorizontal: (root: HTMLElement) => void;
}

const WORD_LIMIT = 32;

const storytelling: StorytellingModule = {
  els: {
    root: Array.from(document.querySelectorAll('.module--storytelling')) as HTMLElement[],
  },

  init() {
    if (this.els.root.length === 0) return;
    this.els.root.forEach((root) => {
      const variation = root.dataset.variation || 'vertical';
      if (variation === 'horizontal') {
        this.bindHorizontal(root);
      } else {
        this.bindDesktop(root);
        this.bindMobile(root);
      }
    });
  },

  bindDesktop(root: HTMLElement) {
    const nav = root.querySelector('.module__navigation');
    const content = root.querySelector('.module__content');
    const triggers = Array.from(root.querySelectorAll('.module__navigation .module__trigger')) as HTMLButtonElement[];

    if (!nav || !content || triggers.length === 0) return;

    // Parse initial data from embedded script
    const dataScript = root.previousElementSibling as HTMLScriptElement | null;
    let data: StoryItem[] = [];
    try {
      if (dataScript && dataScript.type === 'application/json') {
        data = JSON.parse(dataScript.textContent || '[]') as StoryItem[];
      }
    } catch {
      data = [];
    }

    const img = content.querySelector('img') as HTMLImageElement | null;
    const titleEl = content.querySelector('.module__text h2');
    const textEl = content.querySelector('.module__text');
    const ctaEl = content.querySelector('.module__cta');

    const stripHtml = (html: string): string => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return (tmp.textContent || tmp.innerText || '').trim();
    };

    const trimWords = (text: string, maxWords = WORD_LIMIT, ellipsis = '…'): string => {
      const clean = stripHtml(text);
      const words = clean.split(/\s+/).filter(Boolean);
      if (words.length <= maxWords) return clean;
      return words.slice(0, maxWords).join(' ') + ellipsis;
    };

    const setActive = (itemTitle: string) => {
      const item = data.find(d => d.title === itemTitle);
      if (!item) return;

      // Update active class on triggers
      triggers.forEach(btn => btn.classList.toggle('active', btn.dataset.item === itemTitle));

      // Update image if an explicit desktop URL is provided via JSON
      if (img && item.image && item.image.desktop && item.image.desktop.url) {
        img.src = item.image.desktop.url;
        if (item.image.desktop.alt) img.alt = item.image.desktop.alt;
      }

      // Update text (lazy single-paragraph trim)
      if (titleEl) titleEl.textContent = item.title;
      if (textEl) {
        const heading = textEl.querySelector('h2');
        textEl.innerHTML = '';
        if (heading) textEl.appendChild(heading);
        const p = document.createElement('p');
        p.textContent = trimWords(item.text, WORD_LIMIT);
        textEl.appendChild(p);
      }

      // Update CTA
      if (ctaEl) {
        ctaEl.innerHTML = '';
        if (item.button && item.button.url) {
          const type = item.button.type === 'button' ? 'small' : (item.button.type || 'button');
          if (item.button.type === 'link') {
            const a = document.createElement('a');
            a.href = item.button.url;
            a.textContent = item.button.label || 'Learn More';
            ctaEl.appendChild(a);
          } else {
            const a = document.createElement('a');
            a.href = item.button.url;
            a.textContent = item.button.label || 'Learn More';
            a.className = `button button--${type}`;
            ctaEl.appendChild(a);
          }
        }
      }
    };

    triggers.forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        const item = btn.dataset.item || '';
        if (!item) return;
        setActive(item);
      });
    });
  },

  bindMobile(root: HTMLElement) {
    const accordion = root.querySelector('.module__accordion');
    if (!accordion) return;

    const items = Array.from(accordion.querySelectorAll('.module__accordion-item')) as HTMLElement[];
    if (items.length === 0) return;

    items.forEach((wrap) => {
      const header = wrap.querySelector('.module__accordion-header') as HTMLButtonElement | null;
      const panel = wrap.querySelector('.module__accordion-content') as HTMLElement | null;
      if (!header || !panel) return;

      header.addEventListener('click', () => {
        const isOpen = wrap.classList.contains('open');
        // Close all
        items.forEach(w => {
          w.classList.remove('open');
          const h = w.querySelector('.module__accordion-header') as HTMLElement | null;
          const p = w.querySelector('.module__accordion-content') as HTMLElement | null;
          if (h) h.classList.remove('active');
          if (p) p.classList.remove('open');
        });
        // Toggle current
        if (!isOpen) {
          wrap.classList.add('open');
          header.classList.add('active');
          panel.classList.add('open');
        }
      });
    });
  },

  bindHorizontal(root: HTMLElement) {
    const container = root.querySelector('.module__horizontal');
    if (!container) return;

    const items = Array.from(container.querySelectorAll('.module__horizontal-item')) as HTMLElement[];
    if (items.length === 0) return;

    let isAnimating = false;

    const calculateImageWidth = () => {
      const totalWidth = 1360; // Site max width
      const closedItemWidth = 240; // Width of each closed item (200px image + 40px padding)
      const activeItemPadding = 40; // Padding for active item (20px on each side)
      const numClosedItems = items.length - 1; // All items except the active one

      return totalWidth - (closedItemWidth * numClosedItems) - activeItemPadding;
    };

    // Set width on all images - they should be the same size regardless of container
    const imageWidth = calculateImageWidth();
    items.forEach((item) => {
      const img = item.querySelector('.module__horizontal-image img') as HTMLImageElement | null;
      if (img) {
        // Remove any inline styles that might interfere
        img.style.width = `${imageWidth}px`;
        img.style.height = 'auto';
        img.style.maxWidth = 'none';
        img.style.objectFit = 'cover';
      }
    });

    // Three-phase animation handler
    const handleItemClick = async (clickedItem: HTMLElement) => {
      if (isAnimating) return;

      const currentActive = items.find(i => i.classList.contains('active'));
      if (!currentActive || currentActive === clickedItem) return;

      isAnimating = true;

      // Phase 1: Prepare current item for closing (300ms)
      currentActive.classList.add('phase-1-closing');
      await new Promise(resolve => setTimeout(resolve, 300));

      // Phase 2: Width transitions (500ms)
      currentActive.classList.remove('phase-1-closing');
      currentActive.classList.add('phase-2-closing');
      currentActive.classList.remove('active');

      clickedItem.classList.add('phase-2-opening');
      await new Promise(resolve => setTimeout(resolve, 500));

      // Phase 3: Reveal new content (300ms)
      currentActive.classList.remove('phase-2-closing');

      clickedItem.classList.remove('phase-2-opening');
      clickedItem.classList.add('phase-3-revealing');
      clickedItem.classList.add('active');
      await new Promise(resolve => setTimeout(resolve, 300));

      // Cleanup
      clickedItem.classList.remove('phase-3-revealing');
      isAnimating = false;
    };

    items.forEach((item) => {
      item.addEventListener('click', () => handleItemClick(item));
    });
  }
};

export default storytelling;
